import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { pixelY, formatTime, BASE_PX_PER_MINUTE } from '../DiaryContext'
import { movePlacement, finishPlacement, cancelPlacement, clashesAt } from './placement'
import { dateSaid } from './repeat'
import './Repeat.css'

const SNAP = 5

// The visit being placed, in its stylist's column: drag it up or down (or
// tap the column) to choose a time. Amber over another booking, teal when
// the time is free. The bar at the bottom says what it is and confirms.
const PlacementBlock = ({ placement, events, viewStartMinutes, viewEndMinutes, zoomLevel }) => {
  const ref = useRef(null)
  const drag = useRef(null)
  const { minutes, duration, label, date, original, step } = placement
  const unchanged = original != null && minutes === original
  const clashes = clashesAt(minutes, duration, events)
  const busy = clashes.length > 0

  // Bring it into view when it first appears
  useEffect(() => {
    if (ref.current && ref.current.scrollIntoView) ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [])

  const clamp = m => Math.max(viewStartMinutes, Math.min(viewEndMinutes - duration, m))
  const onPointerDown = (e) => {
    e.stopPropagation()
    e.preventDefault()
    drag.current = { y: e.clientY, from: minutes }
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e) => {
    if (!drag.current) return
    const delta = (e.clientY - drag.current.y) / (BASE_PX_PER_MINUTE * zoomLevel)
    const next = clamp(Math.round((drag.current.from + delta) / SNAP) * SNAP)
    if (next !== minutes) movePlacement(next)
  }
  const onPointerUp = (e) => {
    drag.current = null
    if (e.currentTarget.releasePointerCapture) { try { e.currentTarget.releasePointerCapture(e.pointerId) } catch (x) { /* ignore */ } }
  }
  const nudge = (by) => movePlacement(clamp(minutes + by))

  const bar = (
    <div className="rpt-place-bar" role="dialog" aria-label="Choose a new time">
      <div className="rpt-place-bar-text">
        <b>{step && step.total > 1 ? `Clash ${step.index} of ${step.total} · ` : ''}{label ? `${label}, ` : ''}{dateSaid(date)}</b>
        <span className={busy ? 'is-busy' : 'is-free'}>
          {formatTime(minutes)} to {formatTime(minutes + duration)} · {busy ? `Overlaps ${clashes.length} booking${clashes.length === 1 ? '' : 's'}` : 'Free'}
        </span>
        <small>Drag the visit, or tap the column, to pick a time. Only this visit moves.</small>
      </div>
      <div className="rpt-place-bar-btns">
        <button type="button" className="bdp-btn bdp-btn--secondary" onClick={() => cancelPlacement()}>Back to the list</button>
        {step && step.hasNext && (
          <button type="button" className="bdp-btn bdp-btn--secondary" onClick={() => finishPlacement({ next: true })}>{unchanged ? 'Skip to next' : `Use ${formatTime(minutes)}, next`}</button>
        )}
        <button type="button" className="bdp-btn bdp-btn--primary" onClick={() => finishPlacement()}>{unchanged ? `Keep ${formatTime(minutes)}` : `Use ${formatTime(minutes)}`}</button>
      </div>
    </div>
  )

  return (
    <>
      <div
        ref={ref}
        className={`rpt-place-block${busy ? ' is-busy' : ''}${pixelY(duration, 0, zoomLevel) < 44 ? ' is-short' : ''}`}
        style={{ top: pixelY(minutes, viewStartMinutes, zoomLevel), height: pixelY(duration, 0, zoomLevel) }}
        role="slider"
        tabIndex={0}
        aria-label={`New time for ${label || 'this visit'}`}
        aria-valuetext={formatTime(minutes)}
        aria-valuenow={minutes}
        aria-valuemin={viewStartMinutes}
        aria-valuemax={viewEndMinutes - duration}
        onClick={e => e.stopPropagation()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') { e.preventDefault(); nudge(-SNAP) }
          if (e.key === 'ArrowDown') { e.preventDefault(); nudge(SNAP) }
          if (e.key === 'Enter') { e.preventDefault(); finishPlacement() }
          if (e.key === 'Escape') { e.preventDefault(); cancelPlacement() }
        }}
      >
        <span className="rpt-place-time">{formatTime(minutes)}</span>
        <span className="rpt-place-label">{label || 'This visit'}</span>
        <span className="rpt-place-grip" aria-hidden="true" />
      </div>
      {ReactDOM.createPortal(bar, document.body)}
    </>
  )
}

export default PlacementBlock
